/** @jsx jsx */

import { Badge, jsx } from "theme-ui";
import { formatSys2OdooDate } from "@heartfulnessinstitute/react-hfn-forms/dist/formatting";
import { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import DynamicForm from "@heartfulnessinstitute/react-hfn-forms/dist/DynamicForm";
import { ActionButton, DefaultButton } from "office-ui-fabric-react";
import { noop } from "sites-common/utils/lodash";
import ConfirmButton from "gatsby-plugin-hfn-profile/components/ConfirmButton";
import RenderPersonVisit from "./RenderPersonVisit";
import ShortDateRange from "../ShortDateRange";
import FormContext from "./FormContext";
import { IkonButton } from "../StyledButtons";
import { shade0, shade5, good } from "./json/colors.json";
import {
  PIVOT_RECENT,
  STAY_CHECKEDIN,
  STAY_CHECKEDOUT,
  STAY_NONE,
} from "./constants";

const red1 = "#D22B2B";
const red2 = "#EE4B2B";
const red3 = "#FF3131";
const red4 = "orange";
const red5 = "#FFBF00";

const MyBadge = ({ bg, ...restprops }) => (
  <Badge
    sx={{ backgroundColor: bg, m: 1, borderRadius: 5, p: 1, pt: 0 }}
    {...restprops}
  />
);
MyBadge.propTypes = {
  bg: PropTypes.string,
};
MyBadge.defaultProps = {
  bg: "black",
};

const ackform = require("./json/upload_ref.json");
const checkinform = require("./json/checkin.json");
const checkoutform = require("./json/checkout.json");

const ACK = "a";
const CHKIN = "i";
const CHKOUT = "o";
const today = formatSys2OdooDate(new Date());

const RenderRecord = ({ record: r, mistakes }) => {
  const { setP2, onSaveRecord } = useContext(FormContext);

  const [pform, setPform] = useState(null);
  const stayStatus = r.stay_status;
  const notyetcheckedin = !stayStatus;
  const checkedin = !!stayStatus && stayStatus === STAY_CHECKEDIN;
  const checkedout = !!stayStatus && stayStatus === STAY_CHECKEDOUT;

  const overstaying = checkedin && today > r.departure;
  const passportExpired =
    checkedin && today > r.formc?.applicant_passpvalidtill;
  const visaExpired = checkedin && today > r.formc?.applicant_visavalidtill;

  const pendingUpload = (checkedin || checkedout) && !r.uploaded_ref;

  const openAck = useCallback(() => {
    setPform(ACK);
  }, [setPform]);
  const openChkin = useCallback(() => {
    setPform(CHKIN);
  }, [setPform]);
  const openChkout = useCallback(() => {
    setPform(CHKOUT);
  }, [setPform]);
  const onDiscard = useCallback(() => {
    setPform(null);
  }, [setPform]);

  const openPrintDetails = useCallback(() => {
    setP2({
      record: r,
      sections: ["passport", "visa", "travel", "contact"],
      printOnly: true,
    });
  }, [setP2, r]);

  const onCheckin = useCallback(
    (p, s, f) =>
      onSaveRecord(
        { ...p, stay_status: p.stay_status ? p.stay_status : STAY_CHECKEDIN },
        s,
        f
      ),
    [onSaveRecord]
  );
  const onCheckout = useCallback(
    (p, s, f) => onSaveRecord({ ...p, stay_status: STAY_CHECKEDOUT }, s, f),
    [onSaveRecord]
  );

  const resetCheckout = useCallback(
    () => onSaveRecord({ ...r, stay_status: STAY_CHECKEDIN }, noop, noop),
    [r, onSaveRecord]
  );
  const resetCheckin = useCallback(
    () => onSaveRecord({ ...r, stay_status: "" }, noop, noop),
    [r, onSaveRecord]
  );
  const resetUploaded = useCallback(
    () => onSaveRecord({ ...r, uploaded_ref: "" }, noop, noop),
    [r, onSaveRecord]
  );

  useEffect(() => {
    // add Missing Fields
    const defaults = {
      stay_status: STAY_NONE,
    };

    const missingKeys = Object.keys(defaults).filter((k) => !(k in r));
    if (missingKeys.length > 0) {
      const x = {};
      if (!!r.arrival && !!r.departure) {
        x.stay_status = STAY_NONE;
      } else {
        x.invalid = "";
      }
      onSaveRecord({ ...x, ...r }, noop, noop);
    }
  }, [r, onSaveRecord]);

  return (
    <tr sx={{ fontSize: "0.85em" }}>
      <Td>{r.full_name}</Td>

      <Td>
        <ShortDateRange date1={r.arrival} date2={r.departure} />
        {notyetcheckedin && (
          <div sx={{ my: 1 }}>
            {pform !== CHKIN && (
              <ActionButton
                sx={{
                  color: shade0,
                  backgroundColor: red4,
                  py: 0,
                }}
                iconProps={{ iconName: "Forward" }}
                text="Check-In"
                onClick={openChkin}
              />
            )}
            {pform === CHKIN && (
              <div sx={{ backgroundColor: shade5, p: 2 }}>
                <DynamicForm
                  formFields={checkinform}
                  defaultValues={r}
                  onSubmit={onCheckin}
                  onDiscard={onDiscard}
                />
              </div>
            )}
          </div>
        )}
        {mistakes && checkedout && (
          <div sx={{ m: 1 }}>
            <ConfirmButton
              title="Undoing Check-out"
              message="Are You Sure?"
              onClick={resetCheckout}
            >
              <DefaultButton
                iconProps={{ iconName: "Undo" }}
                primary
                text="MOVE TO CHECKED-IN STATE"
              />
            </ConfirmButton>{" "}
          </div>
        )}
        {mistakes && checkedin && (
          <div sx={{ m: 1 }}>
            <ConfirmButton
              title="Undoing Check-in"
              message="Are You Sure?"
              onClick={resetCheckin}
            >
              <DefaultButton
                iconProps={{ iconName: "Undo" }}
                primary
                text="MOVE TO NOT YET CHECKED-IN STATE"
              />
            </ConfirmButton>
          </div>
        )}

        {checkedin && (
          <div sx={{ my: 1 }}>
            {pform !== CHKOUT && (
              <ActionButton
                sx={{
                  color: shade0,
                  backgroundColor: red5,
                  py: 0,
                }}
                iconProps={{ iconName: "Forward" }}
                text="Check-Out"
                onClick={openChkout}
              />
            )}
            {pform === CHKOUT && (
              <div sx={{ backgroundColor: shade5, p: 2 }}>
                <DynamicForm
                  formFields={checkoutform}
                  defaultValues={r}
                  onSubmit={onCheckout}
                  onDiscard={onDiscard}
                />
              </div>
            )}
          </div>
        )}
      </Td>
      {/* <Td>
        <ShortDate date={r.formc?.applicant_passpvalidtill} />
      </Td>
      <Td>
        <ShortDate date={r.formc?.applicant_visavalidtill} />
      </Td> */}
      <Td>
        {overstaying && <MyBadge bg={red1}>overstaying</MyBadge>}
        {passportExpired && <MyBadge bg={red2}>passport expired</MyBadge>}
        {visaExpired && <MyBadge bg={red3}>visa expiring in 30 days</MyBadge>}
        {pendingUpload && <MyBadge bg={red4}>Pending Upload</MyBadge>}
      </Td>
      <RenderPersonVisit
        record={r}
        adminFields={[
          "pdetail",
          "vdetail",
          "tdetail",
          "cdetail",
          "photo",
          "passport",
          "visa",
        ]}
      />
      <Td>
        <IkonButton
          sx={{ color: shade0, backgroundColor: "transparent" }}
          iconProps={{ iconName: "Print" }}
          onClick={openPrintDetails}
        />
      </Td>
      <Td>
        {(checkedin || checkedout) && (
          <div>
            {pform !== ACK && (
              <IkonButton
                sx={{
                  color: shade0,
                  backgroundColor: !pendingUpload ? good : red4,
                }}
                iconProps={{
                  iconName: !pendingUpload ? "Accept" : "Warning12",
                }}
                title={
                  !pendingUpload ? "Available" : "Add Uploaded Reference No"
                }
                onClick={openAck}
              />
            )}
            {mistakes && !pendingUpload && (
              <div sx={{ m: 1 }}>
                <ConfirmButton
                  title="Resetting FRRO Ack No."
                  message="Are You Sure?"
                  onClick={resetUploaded}
                >
                  <DefaultButton
                    primary
                    text="RESET FRRO UPLOAD ACKNOWLEDGMENT NO."
                  />
                </ConfirmButton>
              </div>
            )}
            {pform === ACK && (
              <div sx={{ backgroundColor: shade5, p: 2 }}>
                <DynamicForm
                  formFields={ackform}
                  defaultValues={r}
                  onSubmit={onSaveRecord}
                  onDiscard={onDiscard}
                />
              </div>
            )}
            {r.uploaded_ref}
          </div>
        )}
      </Td>
    </tr>
  );
};

RenderRecord.propTypes = {
  record: PropTypes.shape({
    stay_status: PropTypes.string,
    arrival: PropTypes.string,
    departure: PropTypes.string,
    formc: PropTypes.shape({
      applicant_passpvalidtill: PropTypes.string,
      applicant_visavalidtill: PropTypes.string,
    }),
    uploaded_ref: PropTypes.string,
    full_name: PropTypes.string,
  }).isRequired,
  mistakes: PropTypes.bool.isRequired,
};

const Td = (props) => <td sx={{ px: 1 }} {...props} />;

function RenderPersonVisitsAdmin({ records, mistakes }) {
  const { pivot } = useContext(FormContext);

  return (
    <table>
      <thead>
        <tr sx={{ fontSize: "0.8em", fontWeight: 600 }}>
          <Td>Name</Td>

          <Td>Stay</Td>
          {/* <Td>Passport Validity</Td>
          <Td>Visa Expiry</Td> */}
          <Td> </Td>
          <Td>Passport Detail</Td>
          <Td>Visa Detail</Td>
          <Td>Travel Detail</Td>
          <Td>Contact Detail</Td>
          <Td>Photo</Td>
          <Td>Passport</Td>
          <Td>Visa</Td>
          <Td>Print Details</Td>
          <Td>
            {pivot !== PIVOT_RECENT &&
              "Uploaded to FRRO site / Acknowledgment No."}
          </Td>
        </tr>
      </thead>
      <tbody>
        {records.map((r) => (
          <RenderRecord record={r} mistakes={mistakes} />
        ))}
      </tbody>
    </table>
  );
}

RenderPersonVisitsAdmin.propTypes = {
  records: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mistakes: PropTypes.bool.isRequired,
};
export default RenderPersonVisitsAdmin;
